import { ExperienceKeyMetricFe } from "@intelligems/ig-types/fe-only/custom-events";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { STANDARD_EVENTS_ALLOWED_KEY_METRICS } from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { shortId } from "@intelligems/ig-utils/common";
import { shouldShowProfitPlaceholder } from "@src/features/analytics/utils";

interface MetricDropdownOption {
  title: string;
  id: string;
}

/**
 * Determines which metric should be selected when the metric details view
 * first renders, preferring the experience's primary metric and falling back
 * to the first available dropdown option.
 *
 * Returns `undefined` when there are no metrics to display so callers can
 * render an empty state instead of dereferencing a non-existent option.
 */
export const getInitialMetricId = ({
  primaryMetric,
  dropdownOptions,
  graphConfigs,
  shouldShowProfit,
}: {
  primaryMetric: ExperienceKeyMetricFe | undefined;
  dropdownOptions: MetricDropdownOption[];
  graphConfigs: GraphConfig[];
  shouldShowProfit: boolean;
}): string | undefined => {
  // No metrics available to display — nothing to select.
  if (dropdownOptions.length === 0) {
    return undefined;
  }

  const standardAnalyticsId = primaryMetric?.standardEventId
    ? STANDARD_EVENTS_ALLOWED_KEY_METRICS[primaryMetric.standardEventId]
        ?.analyticsMetricId
    : undefined;

  const customMetricId = primaryMetric?.experienceCustomMetric
    ? shortId(primaryMetric.experienceCustomMetric.customEvent.id)
    : undefined;

  const primaryId = standardAnalyticsId || customMetricId;
  // if no primaryId, fallback to first option
  if (!primaryId) {
    return dropdownOptions[0].id;
  }

  const primaryMetricConfig = graphConfigs.find((config) =>
    config.metrics.includes(primaryId)
  );

  // if the primary metric shouldn't be shown (like profit w/ no COGs), fallback
  if (
    primaryMetricConfig &&
    shouldShowProfitPlaceholder(shouldShowProfit, primaryMetricConfig)
  ) {
    const fallback = dropdownOptions.find((option) => option.id !== primaryId);
    return fallback?.id ?? dropdownOptions[0].id;
  }

  const matchingId = dropdownOptions.find(
    (option) => option.id === primaryId
  )?.id;
  return matchingId ?? dropdownOptions[0].id;
};

export const metricDescriptions = {
  conversion_rate: "Orders divided by unique visitors.",
  net_revenue_per_visitor:
    "Net revenue divided by number of unique visitors. Net revenue includes product and shipping revenue, net of discounts",
  net_product_revenue_per_order:
    "Average net revenue per order generated by product sales alone (i.e. excludes shipping revenue)",
  net_product_revenue:
    "Net revenue generated by product sales alone (i.e., excludes shipping revenue)",
  total_cogs: "Total cost of goods sold",
  total_cost: "Total cost, including COGS, shipping cost, and transaction fees",
  gross_profit:
    "Net revenue minus total cost (COGS, cost of shipping, and transaction fees)",
  gross_margin_pct: "Gross profit divided by net revenue",
  gross_profit_per_visitor:
    "Gross profit (net revenue COGS, cost of shipping, and transaction fees) divided by number of unique visitors",
  gross_profit_per_order: "Gross profit divided by number of orders",
  total_shipping_cost:
    "Cost of shipping (shipping fulfillment cost per order times number of orders)",
  shipping_profit: "Net shipping revenue minus total shipping cost",
  total_transaction_fee: "Transaction fees percent times net revenue",
  net_revenue:
    "Revenue (both product revenue and shipping fees), net of discounts",
  product_profit: "Net product revenue minus COGS",
  net_shipping_revenue_per_order:
    "Revenue from shipping fees, net of discounts, per order",
  pct_orders_free_shipping:
    "Percentage of orders with no associated net shipping revenue",
  n_orders_free_shipping:
    "Number of orders with no associated net shipping revenue",
  n_visitors: "Number of unique visitors",
  n_orders: "Number of orders made by visitors to the experience",
  net_shipping_revenue: "Revenue from shipping fees, net of discounts",
  add_to_cart_rate: "Percentage of visitors who added a product to cart",
  abandoned_cart_rate:
    "Percentage of visitors who added to cart but did not make an order",
  abandoned_checkout_rate:
    "Percentage of visitors who started checkout but did not make an order",
  checkout_begin_rate: "Percentage of visitors who started checkout",
  view_product_page_rate: "Percentage of visitors who viewed a product page",
  view_collection_page_rate:
    "Percentage of visitors who viewed a collection page",
  checkout_enter_contact_info_rate:
    "Percentage of visitors who entered contact information (email or phone number) at checkout",
  checkout_address_submitted_rate:
    "Percentage of visitors who entered their shipping address and saw shipping rates at checkout",
  net_revenue_per_order:
    "Net revenue divided by number of orders (also known as average order value, AOV)",
  avg_units_per_order: "Total unit quantity divided by number of orders",
  avg_product_revenue_per_unit:
    "Net product revenue divided by unit quantity (i.e., average net price paid per unit)",
  pct_discounted_orders: "Percentage of orders with a discount applied",
  avg_discount_per_discounted_order:
    "Average discount per order with a discount applied",
  avg_discount_per_all_orders:
    "Average discount per order, including 0s from orders with no discount",
  discount_pct_discounted_order_gross_revenue:
    "Discounts divided by gross revenue from orders with a discount",
  discount_pct_total_gross_revenue: "Discounts divided by total gross revenue",
  discount_pct_discounted_order_gross_profit:
    "Discounts divided by gross profit from orders with a discount",
  discount_pct_total_gross_profit: "Discounts divided by total gross profit",
  n_orders_discounted: "Number of orders with a discount applied",
  subscription_orders_per_visitor:
    "Number of new subscription orders divided by number of unique visitors",
  subscription_revenue_per_visitor:
    "Net revenue from new subscription orders divided by number of unique visitors",
  subscription_gross_profit_per_visitor:
    "Gross profit from new subscription orders divided by number of unique visitors",
  pct_subscription_orders: "Percentage of orders that contained a subscription",
  subscription_product_revenue_per_order:
    "Average net revenue per new subscription order",
  subscription_shipping_revenue_per_order:
    "Average net revenue from shipping fees per new subscription order",
  subscription_net_revenue:
    "Total revenue from new subscription orders, net of discounts",
  subscription_units_per_subscription_order:
    "Total unit quantity from new subscription orders divided by number of new subscription orders",
  subscription_product_revenue_per_unit:
    "Net product revenue from new subscription orders divided by total unit quantity from new subscription orders",
};
